import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Select, Space, Spin, Alert, Tag, Button, Drawer, Descriptions } from 'antd';
import { message } from '../../../../services/toastMessage';
import { IconWallet } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  getAdminHostDeposits,
  getAdminHostDeposit,
  IAdminHostDepositRow,
  IAdminHostDepositDetail,
} from '../../../../services/AdminService';
import EmptyState from '../../../../components/EmptyState';
import AdminResponsiveList from '../../../../components/Admin/AdminResponsiveList';
import { useAppBreakpoint } from '../../../../hooks/useAppBreakpoint';
import { HOST_DEPOSIT_STATE_OPTIONS, HOST_DEPOSIT_STATE_LABEL_KEYS } from '../../../../constants/adminMoneyOptions';
import { CORAL, TEAL } from '../../../../theme/creamyTokens';
import { TRANSLATIONS } from '../../../../constants/translations';
import { translate } from '../../../../helpers/translationHelper';
import { textSorter, numberSorter } from '../../../../helpers/adminTableHelpers';

const { Title, Text } = Typography;

/**
 * The hosts' own money -- the other deposit, and the one that is the
 * platform's problem rather than a guest's.
 *
 * Balance on its own says almost nothing. What decides whether a host can
 * accept the next booking is the headroom left over after everything they
 * are already on the hook for, so the server computes exposure and the
 * largest booking still allowed with the very same calculator the booking
 * gate uses. A host reading "running low" here is bookings this marketplace
 * is about to start refusing.
 */
const HostDepositsTab: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const [rows, setRows] = useState<IAdminHostDepositRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [state, setState] = useState<string | undefined>(undefined);
  const [detail, setDetail] = useState<IAdminHostDepositDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminHostDeposits(state)
      .then(setRows)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [state]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = (userId: string) => {
    setDetailLoading(true);
    setDetail({} as IAdminHostDepositDetail);
    getAdminHostDeposit(userId)
      .then(setDetail)
      .catch(() => {
        message.error(translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_LOAD_ERROR));
        setDetail(null);
      })
      .finally(() => setDetailLoading(false));
  };

  const renderBadge = (row: IAdminHostDepositRow) => {
    if (row.closedAt) {
      return <Tag style={{ marginInlineEnd: 0 }}>{translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_CLOSED_BADGE)}</Tag>;
    }
    if (row.isLow) {
      return <Tag color="red" style={{ marginInlineEnd: 0 }}>{translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_LOW_BADGE)}</Tag>;
    }
    return null;
  };

  const renderActions = (row: IAdminHostDepositRow) => (
    <Button size="small" onClick={() => openDetail(row.userId)}>
      {translate(t, TRANSLATIONS.ADMIN_HOSTS_VIEW_BUTTON)}
    </Button>
  );

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_STATE_FILTER_ALL)}
          style={{ maxWidth: 220, width: '100%' }}
          value={state}
          onChange={setState}
          options={HOST_DEPOSIT_STATE_OPTIONS.map((value) => ({ value, label: translate(t, HOST_DEPOSIT_STATE_LABEL_KEYS[value]) }))}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_LOAD_ERROR)} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<IconWallet size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminHostDepositRow>
          rowKey="userId"
          dataSource={rows}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(row) => ({
            key: row.userId,
            title: row.hostName || row.userId,
            subtitle: `${row.balance.toFixed(2)} ${row.currencyCode}`,
            badge: renderBadge(row),
            fields: [
              { label: translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_COL_FUNDED), value: `${row.totalFunded.toFixed(2)} ${row.currencyCode}` },
              {
                label: translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_COL_EXPOSURE),
                value: `${row.currentExposure.toFixed(2)} ${row.currencyCode}`,
              },
              {
                label: translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_COL_MAX_BOOKING),
                value: `${row.maxBookingValue.toFixed(2)} ${row.currencyCode}`,
                wide: true,
              },
            ],
            actions: renderActions(row),
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_HOST),
              dataIndex: 'hostName',
              key: 'hostName',
              sorter: textSorter<IAdminHostDepositRow>('hostName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_COL_BALANCE),
              dataIndex: 'balance',
              key: 'balance',
              sorter: numberSorter<IAdminHostDepositRow>('balance'),
              render: (value: number, row: IAdminHostDepositRow) => (
                <Text strong style={{ color: row.isLow ? CORAL : TEAL }}>
                  {value.toFixed(2)} {row.currencyCode}
                </Text>
              ),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_COL_FUNDED),
              dataIndex: 'totalFunded',
              key: 'totalFunded',
              sorter: numberSorter<IAdminHostDepositRow>('totalFunded'),
              render: (value: number, row: IAdminHostDepositRow) => `${value.toFixed(2)} ${row.currencyCode}`,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_COL_EXPOSURE),
              dataIndex: 'currentExposure',
              key: 'currentExposure',
              sorter: numberSorter<IAdminHostDepositRow>('currentExposure'),
              render: (value: number, row: IAdminHostDepositRow) => `${value.toFixed(2)} ${row.currencyCode}`,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_COL_MAX_BOOKING),
              dataIndex: 'maxBookingValue',
              key: 'maxBookingValue',
              sorter: numberSorter<IAdminHostDepositRow>('maxBookingValue'),
              render: (value: number, row: IAdminHostDepositRow) => `${value.toFixed(2)} ${row.currencyCode}`,
            },
            {
              title: '',
              key: 'badge',
              render: (_: unknown, row: IAdminHostDepositRow) => renderBadge(row),
            },
            { title: '', key: 'actions', render: (_: unknown, row: IAdminHostDepositRow) => renderActions(row) },
          ]}
        />
      )}

      <Drawer
        title={translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_DETAIL_TITLE)}
        open={detail !== null}
        onClose={() => setDetail(null)}
        width={isMobile ? '100%' : 480}
      >
        {detailLoading || !detail?.userId ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : (
          <>
            <Descriptions column={1} bordered size="small" style={{ marginBottom: 20 }}>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_HOST)}>{detail.hostName}</Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_COL_BALANCE)}>
                {detail.balance.toFixed(2)} {detail.currencyCode}
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_COL_FUNDED)}>
                {detail.totalFunded.toFixed(2)} {detail.currencyCode}
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_COL_EXPOSURE)}>
                {detail.currentExposure.toFixed(2)} {detail.currencyCode}
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_COL_MAX_BOOKING)}>
                {detail.maxBookingValue.toFixed(2)} {detail.currencyCode}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ marginBottom: 8 }}>
              {translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_MOVEMENTS)}
            </Title>
            {detail.movements.length === 0 ? (
              <Text type="secondary">{translate(t, TRANSLATIONS.ADMIN_HOST_DEPOSITS_MOVEMENTS_EMPTY)}</Text>
            ) : (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {detail.movements.map((movement) => (
                  <div key={movement.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0 }}>
                      <Text strong style={{ fontSize: 13 }}>
                        {movement.kind}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                        {new Date(movement.createdAt).toLocaleString()}
                        {movement.invoiceNumber ? ` · ${movement.invoiceNumber}` : ''}
                      </Text>
                      {movement.reason && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', overflowWrap: 'anywhere' }}>
                          {movement.reason}
                        </Text>
                      )}
                    </div>
                    {/* Signed, because the direction is the whole meaning: a
                        top-up and a cancellation charge are the same number
                        with opposite consequences. */}
                    <Text strong style={{ fontSize: 13, whiteSpace: 'nowrap', color: movement.amount < 0 ? CORAL : TEAL }}>
                      {movement.amount > 0 ? '+' : ''}
                      {movement.amount.toFixed(2)} {detail.currencyCode}
                    </Text>
                  </div>
                ))}
              </Space>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default HostDepositsTab;
